import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiLoader } from 'react-icons/fi';
import { cPost } from '../../api';
import { useLanguage } from '../../i18n';
import AuthorProfileCard from '../../components/community/AuthorProfileCard';
import CommentList from '../../components/community/CommentList';
import CommunitySidebar from '../../components/community/CommunitySidebar';
import ProductRail from '../../components/community/ProductRail';
import ShopsRail from '../../components/community/ShopsRail';
import PostCard from '../../components/community/PostCard';
import { BLUE } from '../../components/community/brand';

/**
 * One article + its comments (full text, every section — no clamping) and the
 * **full profile of the member who wrote it** in the right sidebar.
 */
export default function PostDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * “Back to feed” walks the real history when the reader came from the feed —
   * that is what makes the list + reading position come back (see
   * `utils/feedCache`). Opened from a shared link instead? Go to the feed.
   */
  const goBack = (e) => {
    e.preventDefault();
    const idx = (window.history.state && window.history.state.idx) || 0;
    if (idx > 0) navigate(-1);
    else navigate('/');
  };

  useEffect(() => {
    setLoading(true);
    cPost(id)
      .then(setPost)
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
        <FiLoader className="w-5 h-5 animate-spin" /> {t('loading')}
      </div>
    );
  }

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-bold">{t('notFound404')}</p>
        <Link to="/" onClick={goBack} className="inline-flex items-center gap-2 mt-4 text-white px-4 py-2 rounded-full font-bold" style={{ background: BLUE }}>
          <FiArrowLeft /> {t('backToFeed')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] px-3 lg:px-4 py-4 grid grid-cols-1 lg:grid-cols-[230px_minmax(0,1fr)_230px] xl:grid-cols-[270px_minmax(0,1fr)_270px] gap-4 xl:gap-5 items-start">
      {/* Same shell as the feed: left menu · centred article + comments · right rail */}
      <CommunitySidebar>
        <ShopsRail />
      </CommunitySidebar>

      <div className="min-w-0 space-y-3">
        <Link to="/" onClick={goBack} className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-300 hover:underline">
          <FiArrowLeft /> {t('backToFeed')}
        </Link>

        <PostCard post={post} commentsOpen onChanged={setPost} />

        {/* On phones the author profile is hidden so the comments come first */}
        <section className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
          <h2 className="font-extrabold text-sm mb-1">
            {t('comments')} ({post.comment_count || 0})
          </h2>
          <CommentList
            postId={post.id}
            postOwnerId={post.user_id}
            canComment={post.allow_comments !== false}
            onCount={(delta) => setPost((prev) => (prev ? { ...prev, comment_count: Math.max(0, (prev.comment_count || 0) + delta) } : prev))}
          />
        </section>
      </div>

      {/* Right sidebar — the author's profile + the shop products */}
      <aside className="hidden lg:block no-scrollbar space-y-4 sticky top-[72px] max-h-[calc(100vh-88px)] overflow-y-auto overscroll-contain">
        <AuthorProfileCard userId={post.user_id} author={post.author} />
        <ProductRail limit={3} />
      </aside>
    </div>
  );
}
